import { z } from "zod";

import { getGroupConfigSubdomain } from "@/configuration/paths";
import { ConfigSubdomain } from "@/configuration/tenant-ids";
import { RecommendationType, SchedulingRecommendationMode } from "@/shared";
import { logger } from "@/src/lib/logging/logger";
import { npiNumberSchema } from "@/src/models/common-schema";
import { DatabaseDefaults } from "@/src/models/database-defaults";
import { Group } from "@/src/models/group";

const availabilityAppointmentSlotSchema = z.object({
  appointmentId: z.union([z.string(), z.number().transform(String)]),
  appointmentType: z.string(),
  ehrAppointmentTypeId: z.string().nullable(),
  start: z.string(),
  end: z.string(),
  practitionerId: z.string().nullable(),
  practitionerFirstName: z.string().nullable(),
  practitionerLastName: z.string().nullable(),
  practitionerDisplayName: z.string().nullable(),
  locationId: z.string().nullable(),
  locationName: z.string().nullable(),
});

const rankedAppointmentSchema = z.object({
  rank: z.number().nullish(),
  providerName: z.string().nullish(),
  locationName: z.string().nullish(),
  reasoning: z.string().nullish(),
  appointmentSlot: availabilityAppointmentSlotSchema,
});

const renderingProviderMatchSchema = z.object({
  providerEhrId: z.string(),
  npi: z.string(),
  displayName: z.string(),
  specialty: z
    .enum(["cardiology", "vascular", "electrophysiology"])
    .transform((s) => (s === "electrophysiology" ? ("cardiology" as const) : s)),
});

const upcomingConflictResultSchema = z.object({
  hasUpcomingWithinWindow: z.boolean(),
  doNotScheduleAnother: z.boolean(),
  escalate: z.boolean(),
  reason: z.string(),
});

const recentSameSpecialtyVisitResultSchema = z.object({
  ineligible: z.boolean(),
  reason: z.string(),
});

const chartEligibilityCheckSchema = z.object({
  classification: z.enum(["established", "new_with_upcoming", "never_seen"]),
  renderingProviders: z.array(renderingProviderMatchSchema).nullish(),
  upcomingConflict: upcomingConflictResultSchema.nullable(),
  transferOfCareEscalation: z.boolean(),
  transferOfCareReason: z.string().nullish(),
  recentSameSpecialtyVisit: recentSameSpecialtyVisitResultSchema.nullable(),
  reasons: z.array(z.string()),
});

export const NO_RECOMMENDATION_REASON_CATEGORIES = [
  "no_open_slots",
  "no_eligible_providers_or_locations",
  "patient_choice_required",
  "no_candidate_pairs",
  "receiving_location_not_approved",
  "rendering_provider_not_schedulable",
  "upcoming_appointment_conflict",
  "recent_same_specialty_visit",
  "transfer_of_care_escalation",
] as const;

export type NoRecommendationReasonCategory = (typeof NO_RECOMMENDATION_REASON_CATEGORIES)[number];

const noRecommendationReasonSchema = z.object({
  category: z.enum(NO_RECOMMENDATION_REASON_CATEGORIES),
  explanation: z.string(),
});

export const ALL_PROVIDERS_NPI = "*";

const practitionerNpiSchema = z.union([npiNumberSchema, z.literal(ALL_PROVIDERS_NPI)]);

export enum SchedulingIneligibilityReason {
  CARDIAC_CLEARANCE = "Cardiac Clearance",
  NEW_IMAGING_TESTING = "New Imaging/Testing",
  UNSUPPORTED_DIAGNOSIS = "Unsupported Diagnosis",
  TRANSFER_OF_CARE = "Transfer of Care",
  RECENT_APPT = "Recent Appointment",
  SELF_PAY_NOT_ACCEPTED = "Self-Pay Not Accepted",
  MINOR_PATIENT = "Minor Patient",
  UNSUPPORTED_LANGUAGE = "Unsupported Language",
  INMATE = "Inmate",
  INSURANCE_NOT_ACCEPTED = "Insurance Not Accepted",
}

export function isSchedulingIneligibilityReason(value: string): value is SchedulingIneligibilityReason {
  return (Object.values(SchedulingIneligibilityReason) as string[]).includes(value);
}

// Coarse categories for the single "primary escalation reason" persisted on a not-recommended
// recommendation (USHV-Pima). Distinct from SchedulingIneligibilityReason: these are the rule
// *categories* an LLM classifies the eligibility result into, not the specific ineligibility codes.
export const PIMA_ESCALATION_REASONS = [
  "Service line",
  "Diagnosis",
  "Reason for referral",
  "Language",
  "Age",
  "Upcoming appointment",
  "Past appointment",
  "Priority",
  "Receiving Provider",
  "Referring Provider",
  "Receiving Location",
] as const;

export type PimaEscalationReason = (typeof PIMA_ESCALATION_REASONS)[number];

export function isPimaEscalationReason(value: string): value is PimaEscalationReason {
  return (PIMA_ESCALATION_REASONS as readonly string[]).includes(value);
}

export const eligibilitySchema = z.object({
  eligible: z.boolean(),
  reasons: z.array(z.string()),
  reasonCodes: z.array(z.string()).optional(),
});

export type Eligibility = z.infer<typeof eligibilitySchema>;

export const agenticPractitionerRecommendationSchema = z.object({
  npiNumber: practitionerNpiSchema,
  providerDisplayName: z.string(),
  newPatientScheduling: eligibilitySchema.optional(),
  establishedPatientScheduling: eligibilitySchema.optional(),
  rescheduling: eligibilitySchema.optional(),
  reason: z.array(z.string()),
  reasonCodes: z.array(z.string()),
  shouldSchedule: z.boolean(),
  isReceivingProvider: z.boolean(),
  prompt: z.string(),
});

export type AgenticPractitionerRecommendation = z.infer<typeof agenticPractitionerRecommendationSchema>;

const defaultActionEligibility: Eligibility = {
  eligible: false,
  reasons: [],
};

export const agenticPractitionerRecommendationShape: AgenticPractitionerRecommendation = {
  npiNumber: "1234567890",
  providerDisplayName: "",
  newPatientScheduling: defaultActionEligibility,
  establishedPatientScheduling: defaultActionEligibility,
  rescheduling: defaultActionEligibility,
  reason: [],
  reasonCodes: [],
  shouldSchedule: false,
  isReceivingProvider: false,
  prompt: "",
};

export const agenticRecommendationSchema = z.object({
  evaluatedPractitioners: z.array(agenticPractitionerRecommendationSchema),
  evaluatedAt: z.string(),
});

export type AgenticRecommendation = z.infer<typeof agenticRecommendationSchema>;

export const agenticRecommendationShape: AgenticRecommendation = {
  evaluatedPractitioners: [agenticPractitionerRecommendationShape],
  evaluatedAt: "",
};

// Reasons a reviewer can give for marking a scheduling recommendation incorrect.
// `other` requires the free-text `other` field to be filled in.
export const SCHEDULING_VERIFICATION_REASON_CODES = [
  "insurance",
  "diagnosis",
  "location",
  "provider",
  "specialty",
  "ehr_sync",
  "other",
] as const;

export type SchedulingVerificationReasonCode = (typeof SCHEDULING_VERIFICATION_REASON_CODES)[number];

export const SCHEDULING_VERIFICATION_REASON_LABELS: Record<SchedulingVerificationReasonCode, string> = {
  insurance: "Insurance issue",
  diagnosis: "Diagnosis issue",
  location: "Location issue",
  provider: "Provider issue",
  specialty: "Specialty issue",
  ehr_sync: "EHR sync issue",
  other: "Other",
};

export const verificationReasonSchema = z
  .object({
    reasons: z.array(z.enum(SCHEDULING_VERIFICATION_REASON_CODES)).min(1),
    other: z.string().nullable(),
  })
  .refine((value) => !value.reasons.includes("other") || Boolean(value.other?.trim()), {
    message: "Describe the reason when selecting 'Other'",
    path: ["other"],
  });

export type VerificationReason = z.infer<typeof verificationReasonSchema>;

// Request body for marking a scheduling recommendation correct/incorrect. verificationReason is
// required (and only allowed) when marking incorrect, so the stored reason can never drift from the
// isCorrect flag. Shared by the referral inline verification route and the dedicated verification task.
const verifySchedulingRecommendationShape = z.object({
  isCorrect: z.boolean().nullable(),
  verificationReason: verificationReasonSchema.nullable(),
});

function refineVerificationReason(value: z.infer<typeof verifySchedulingRecommendationShape>): boolean {
  return value.isCorrect === false ? value.verificationReason !== null : value.verificationReason === null;
}

const verifySchedulingRecommendationRefinement = {
  message: "verificationReason is required when marking incorrect and must be empty otherwise",
  path: ["verificationReason"],
};

export const verifySchedulingRecommendationSchema = verifySchedulingRecommendationShape.refine(
  refineVerificationReason,
  verifySchedulingRecommendationRefinement
);

export type VerifySchedulingRecommendationInput = z.infer<typeof verifySchedulingRecommendationSchema>;

export const verifyReferralSchedulingRecommendationSchema = verifySchedulingRecommendationShape
  .extend({
    recommendationId: z.string().min(1),
  })
  .refine(refineVerificationReason, verifySchedulingRecommendationRefinement);

export type VerifyReferralSchedulingRecommendationInput = z.infer<typeof verifyReferralSchedulingRecommendationSchema>;

export const schedulingRecommendationSchema = z.object({
  patientId: z.string(),
  referralId: z.string().nullable(),
  taskId: z.string().nullable(),
  agenticRecommendation: agenticRecommendationSchema,
  systemInstruction: z.string(),
  rulesetHash: z.string().nullable(),
  seenPractitionersKey: z.string().nullable(),
  // Human verification of the recommendation shown in the referral task UI.
  // TRUE = correct, FALSE = incorrect, null = not yet verified. Gates auto SMS outreach.
  isCorrect: z.boolean().nullable(),
  // Reviewer's reason(s) for an incorrect verification; null unless isCorrect === false.
  verificationReason: verificationReasonSchema.nullable(),
  // Persisted referral-task "should schedule" verdict at write time; null = not computed.
  isEligible: z.boolean().nullable(),
  recommendedAppointment: rankedAppointmentSchema.nullable().optional(),
  noRecommendationReason: noRecommendationReasonSchema.nullable().optional(),
  chartEligibilityCheck: chartEligibilityCheckSchema.nullable().optional(),
  // Single primary escalation reason (USHV-Pima), LLM-classified from the eligibility result when
  // the recommendation is not recommended for scheduling; null otherwise.
  escalationReason: z.enum(PIMA_ESCALATION_REASONS).nullable().optional(),
  // Scheduling channel this recommendation was generated for
  mode: z.nativeEnum(SchedulingRecommendationMode).nullable().optional(),
});

export type SchedulingRecommendation = z.infer<typeof schedulingRecommendationSchema> & DatabaseDefaults;

function isEvaluationRecommendedForType(
  evaluation: AgenticPractitionerRecommendation,
  recommendationType: RecommendationType,
  recommendationId: string
): boolean {
  if (recommendationType === RecommendationType.ANY) return evaluation.shouldSchedule;

  const recommendationTypeEval = evaluation[recommendationType];

  if (recommendationTypeEval == null) {
    logger.warn(
      "No evaluation found for SchedulingType; evaluation schema is outdated. Falling back to shouldSchedule.",
      {
        recommendationId,
        actionType: recommendationType,
      }
    );

    return evaluation.shouldSchedule;
  }

  return recommendationTypeEval.eligible;
}

export function isRecommendedForScheduling(
  recommendation: SchedulingRecommendation | null,
  recommendationType: RecommendationType = RecommendationType.ANY
) {
  if (!recommendation) return false;

  return recommendation.agenticRecommendation.evaluatedPractitioners.some((evaluation) =>
    isEvaluationRecommendedForType(evaluation, recommendationType, recommendation.id)
  );
}

/**
 * Whether the recommendation recommends scheduling a SPECIFIC provider (by NPI) for the given
 * scenario. Unlike isRecommendedForScheduling (which is satisfied by any evaluated provider), this
 * scopes the verdict to one provider — used by autobook, which must confirm the referral's receiving
 * provider in particular is recommended for the patient's recommendationType.
 */
export function isProviderRecommendedForScheduling(
  recommendation: SchedulingRecommendation | null,
  npiNumber: string | null | undefined,
  recommendationType: RecommendationType = RecommendationType.ANY
): boolean {
  if (!recommendation || !npiNumber) return false;

  const evaluation = recommendation.agenticRecommendation.evaluatedPractitioners.find(
    (candidate) => candidate.npiNumber === npiNumber
  );

  if (!evaluation) return false;

  return isEvaluationRecommendedForType(evaluation, recommendationType, recommendation.id);
}

export function shouldRecommendForSchedulingInReferralTask(
  group: Group | null,
  recommendation: SchedulingRecommendation | null
): boolean {
  if (!group) {
    return isRecommendedForScheduling(recommendation, RecommendationType.NEW_PATIENT);
  }

  switch (getGroupConfigSubdomain(group)) {
    case ConfigSubdomain.HEARTPLACE:
    case ConfigSubdomain.USHV_PIMA:
    case ConfigSubdomain.SPORTORTHO:
    case ConfigSubdomain.BALANCE_HEALTH:
      return (
        isRecommendedForScheduling(recommendation, RecommendationType.NEW_PATIENT) ||
        isRecommendedForScheduling(recommendation, RecommendationType.ESTABLISHED_PATIENT)
      );
    default:
      return isRecommendedForScheduling(recommendation, RecommendationType.NEW_PATIENT);
  }
}

export const schedulingRecommendationShape: SchedulingRecommendation = {
  id: "",
  groupId: "",
  createdAt: "",
  updatedAt: "",
  deletedAt: null,
  patientId: "",
  referralId: null,
  taskId: null,
  agenticRecommendation: agenticRecommendationShape,
  systemInstruction: "",
  rulesetHash: null,
  seenPractitionersKey: null,
  isCorrect: null,
  verificationReason: null,
  isEligible: null,
  mode: null,
};
